import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { createClient } from "@repo/backend/client";
import { getContext, setContext } from "svelte";
import { resolveURL } from "./route.svelte.js";
import { currentRelativeURL } from "./url.svelte.js";
import { urlQueryCodec, z } from "@repo/utils";
import { toast } from "svelte-sonner";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}
export type WithoutChild<T> = T extends { child?: any } ? Omit<T, "child"> : T;
export type WithoutChildren<T> = T extends { children?: any } ? Omit<T, "children"> : T;
export type WithoutChildrenOrChild<T> = WithoutChildren<WithoutChild<T>>;
export type WithElementRef<T, U extends HTMLElement = HTMLElement> = T & { ref?: U | null };

export function getClient(options?: { fetch?: typeof window.fetch; fetchOptions?: RequestInit; timeoutMs?: number }) {
    const url = new URL(window.location.toString());
    return createClient({
        baseURL: url.origin,
        fetch: options?.fetch,
        timeoutMs: options?.timeoutMs ?? 60000,
        fetchOptions: {
            redirect: "manual",
            ...options?.fetchOptions,
        },
        tryParseResponse: async (response: Response) => {
            if (response.status === 401 || response.status === 403) {
                goto({ route: "/signin", replace: false, query: { dest: currentRelativeURL() } });
            }

            if (response.status === 400 && response.headers.get("content-type") === "application/json") {
                const responseBody = await response.json();
                const error = z.object({ error: z.object({ message: z.string() }) }).safeParse(responseBody);
                if (error.success) {
                    toast.error(error.data.error.message);
                    throw new Error(error.data.error.message);
                }
                throw new Error(JSON.stringify(responseBody));
            }

            return [false];
        },
    });
}

export function deleteCookie(name: string) {
    document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
}

export function goto(args: {
    route: string;
    params?: Record<string, string | string[]>;
    replace: boolean;
    query?: Record<string, string | string[]>;
}) {
    const u = resolveURL(args.route, args.params);
    if (args.query) {
        u.search = z.encode(urlQueryCodec(), args.query).toString();
    }
    if (args.replace) {
        window.history.replaceState(null, "", u);
    } else {
        window.history.pushState(null, "", u);
    }
}

export function createContext<T>() {
    const key = {};
    return {
        set(value: T) {
            setContext(key, value);
        },
        get() {
            return getContext<T>(key);
        },
    };
}

export function getHighlightSegments(
    text: string | null | undefined,
    query: string,
): { text: string; highlight: boolean; index: number }[] {
    if (!text || !query.trim()) {
        return [{ text: text ?? "-", highlight: false, index: 0 }];
    }
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    const index = lowerText.indexOf(lowerQuery);
    if (index === -1) {
        return [{ text, highlight: false, index: 0 }];
    }
    const segments: { text: string; highlight: boolean; index: number }[] = [];
    if (index > 0) {
        segments.push({ text: text.slice(0, index), highlight: false, index: 0 });
    }
    segments.push({
        text: text.slice(index, index + lowerQuery.length),
        highlight: true,
        index: index,
    });
    if (index + lowerQuery.length < text.length) {
        segments.push({
            text: text.slice(index + lowerQuery.length),
            highlight: false,
            index: index + lowerQuery.length,
        });
    }
    return segments;
}
